import type { Order, OrderStatus, OrderFilters } from './models/order.models';
import type { PaginationData } from '../shared/components/pagination-bar/pagination-bar.component';
import { OrdersService } from './services/orders.service';
import { OrderUtils } from './utils/order.utils';
import {
  buildPaginationData,
  calculateTotalPages,
  hasActiveFiltersFromFilters,
  hasMainFiltersSelectedFromFilters,
  hasFilterChangesFromFilters,
  countActiveOrderFilters,
  toggleOrderIdSort,
  toggleDateTimeSort,
  getOrdersSortIcon,
  isValidPageNumber,
  type OrdersSortToggleState,
} from './utils/orders-page-shared.utils';

type SortDirection = 'asc' | 'desc' | 'none';

/**
 * Shared order table helpers (status/wash type/source/format, pagination, filter getters).
 * Extend by admin-orders, staff-orders, tunnel-orders to remove duplicated method bodies.
 * Subclass must have: ordersService, currentPage, totalItems, itemsPerPage, appliedFilters, searchTerm, loadOrders().
 */
export abstract class OrderListTableBase {
  abstract ordersService: OrdersService;
  abstract currentPage: number;
  abstract totalItems: number;
  abstract itemsPerPage: number;
  abstract orders: Order[];
  abstract sortColumn: string | null;
  abstract sortDirection: SortDirection;
  abstract get appliedFilters(): OrderFilters;
  abstract get searchTerm(): string;
  abstract set searchTerm(value: string);
  abstract loadOrders(): void;

  get hasActiveFilters(): boolean {
    return hasActiveFiltersFromFilters(this.appliedFilters);
  }

  /** Override in staff-orders to add selectedLocationId check. */
  hasSearchOrFiltersExtra(): boolean {
    return false;
  }

  get hasSearchOrFilters(): boolean {
    return (
      (Boolean(this.searchTerm) && this.searchTerm.trim().length > 0) ||
      this.hasActiveFilters ||
      this.hasSearchOrFiltersExtra()
    );
  }

  get hasMainFiltersSelected(): boolean {
    return hasMainFiltersSelectedFromFilters(this.appliedFilters);
  }

  get hasFilterChanges(): boolean {
    return hasFilterChangesFromFilters(this.appliedFilters);
  }

  getActiveFilterCount(): number {
    return countActiveOrderFilters(this.appliedFilters);
  }

  clearSearch(): void {
    this.searchTerm = '';
    this.currentPage = 1;
    this.loadOrders();
  }

  getStatusClass(status: OrderStatus): string {
    return this.ordersService.getStatusClass(status);
  }

  getWashTypeClass(washType: string, washTypeValue?: string): string {
    return this.ordersService.getWashTypeClass(washType, washTypeValue);
  }

  getCapitalizedSource(source: string | undefined): string {
    return OrderUtils.capitalizeSource(source);
  }

  formatPaymentMethod(paymentMethod: string | undefined): string {
    return OrderUtils.formatPaymentMethod(paymentMethod);
  }

  get filteredOrders(): Order[] {
    return this.orders;
  }

  /** Orders are server-paginated; the loaded page is shown as-is. */
  get paginatedOrders(): Order[] {
    return this.filteredOrders;
  }

  get paginationData(): PaginationData {
    return buildPaginationData(
      this.currentPage,
      this.totalPages,
      this.totalItems,
      this.itemsPerPage
    );
  }

  get totalPages(): number {
    return calculateTotalPages(this.totalItems, this.itemsPerPage);
  }

  get paginationInfo(): string {
    return this.ordersService.getPaginationInfo(
      this.currentPage,
      this.itemsPerPage,
      this.totalItems
    );
  }

  onPageChange(page: number): void {
    if (!isValidPageNumber(page, this.totalPages)) {
      return;
    }
    this.currentPage = page;
    this.loadOrders();
  }

  onItemsPerPageChange(newItemsPerPage: number): void {
    this.itemsPerPage = newItemsPerPage;
    this.currentPage = 1;
    this.loadOrders();
  }

  protected getSortToggleState(): OrdersSortToggleState {
    return {
      sortColumn: this.sortColumn ?? '',
      sortDirection: this.sortDirection,
      orders: this.orders,
    };
  }

  sortByOrderId(): void {
    const state = this.getSortToggleState();
    toggleOrderIdSort(state, this.ordersService);
    this.sortColumn = state.sortColumn;
    this.sortDirection = state.sortDirection;
    this.orders = state.orders;
  }

  sortByDateTime(): void {
    const state = this.getSortToggleState();
    toggleDateTimeSort(state, this.ordersService);
    this.sortColumn = state.sortColumn;
    this.sortDirection = state.sortDirection;
    this.orders = state.orders;
  }

  getSortIcon(column: string): string {
    return getOrdersSortIcon(column, this.sortColumn ?? '', this.sortDirection);
  }
}
